import { memo, useState, useCallback, useMemo, useEffect } from "react";
import { X, Check, Eye, PenLine, FunctionSquare, Image, Triangle, Pencil, Plus, Minus } from "lucide-react";
import { sanitizeSvg } from "../../../../../../utils/sanitize";
import ChoixRenderer from "../../../../../VueQuestion/ChoixRenderer";
import ExpressionNumeriqueElement from "../Elements/ExpressionNumeriqueElement";
import ImageElement from "../Elements/ImageElement";
import NumberLineElement from "../Elements/NumberLineElement";

const LETTRES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
const MIN_RESPONSES = 2;
const MAX_RESPONSES = 8;

const RESPONSE_ELEMENT_TYPES = [
    { type: 'expression', icon: <FunctionSquare size={13} />, label: 'Math' },
    { type: 'image', icon: <Image size={13} />, label: 'Image' },
    { type: 'numberline', icon: <span className="text-[11px]">📏</span>, label: 'Droite' },
    { type: 'geogebra', icon: <Triangle size={13} />, label: 'GeoGebra' },
];

const emptyResponse = () => ({ mode: 'text', contenu: '', element: null });

const isResponseFilled = (r) => {
    if (r.mode === 'element' && r.element) return true;
    if (r.mode === 'text' && r.contenu.trim()) return true;
    return false;
};

const ELEMENT_EDITORS = {
    expression: ExpressionNumeriqueElement,
    image: ImageElement,
    numberline: NumberLineElement,
};

function QCMResponseEditor({
    responses,
    setResponses,
    correctAnswerIndexes,
    setCorrectAnswerIndexes,
    showPreview,
    onTogglePreview,
    onOpenGeogebra,
    onValidChange,
}) {
    const hasElementResponse = responses.some(r => r.mode === 'element' && r.element);
    // Multi = plus d'une réponse cochée (state interne dérivé)
    const [isMulti, setIsMulti] = useState(correctAnswerIndexes.size > 1);

    // Détection des doublons parmi les réponses remplies
    const hasDuplicates = useMemo(() => {
        const filledValues = responses
            .filter(isResponseFilled)
            .map(r => r.mode === 'element' ? JSON.stringify(r.element) : r.contenu.trim().toLowerCase());
        return filledValues.length !== new Set(filledValues).size;
    }, [responses]);

    // Validation — au moins MIN_RESPONSES remplies + bonne réponse cochée + pas de doublons
    const filledCount = useMemo(() => responses.filter(isResponseFilled).length, [responses]);
    const isValid = useMemo(() => {
        const enoughFilled = filledCount >= MIN_RESPONSES;
        const hasCorrect = correctAnswerIndexes.size > 0 &&
            [...correctAnswerIndexes].every(idx => idx >= 0 && idx < responses.length && isResponseFilled(responses[idx]));
        return enoughFilled && hasCorrect && !hasDuplicates;
    }, [filledCount, responses, correctAnswerIndexes, hasDuplicates]);

    useEffect(() => {
        onValidChange(isValid);
    }, [isValid, onValidChange]);

    // Handler de sélection — toggle en multi, remplacement en simple
    const handleSelectCorrect = useCallback((index) => {
        if (isMulti) {
            setCorrectAnswerIndexes(prev => {
                const next = new Set(prev);
                if (next.has(index)) next.delete(index);
                else next.add(index);
                return next;
            });
        } else {
            // Simple : remplace (un seul coché)
            setCorrectAnswerIndexes(new Set([index]));
        }
    }, [isMulti, setCorrectAnswerIndexes]);

    // Handler toggle multi
    const handleToggleMulti = useCallback((multi) => {
        setIsMulti(multi);
        if (!multi && correctAnswerIndexes.size > 1) {
            // Multi → Simple : garder seulement le premier
            setCorrectAnswerIndexes(new Set([[...correctAnswerIndexes][0]]));
        }
    }, [correctAnswerIndexes, setCorrectAnswerIndexes]);

    const handleResponseTextChange = useCallback((index, value) => {
        setResponses(prev => {
            const next = [...prev];
            next[index] = { ...next[index], contenu: value };
            return next;
        });
    }, [setResponses]);

    const handleResetToText = useCallback((index) => {
        setResponses(prev => {
            const next = [...prev];
            next[index] = emptyResponse();
            return next;
        });
    }, [setResponses]);

    const handleSetElement = useCallback((index, elementType) => {
        setResponses(prev => {
            const next = [...prev];
            next[index] = { mode: 'element', contenu: '', element: { type: elementType, config: {} } };
            return next;
        });
        if (elementType === 'geogebra') onOpenGeogebra?.(index);
    }, [setResponses, onOpenGeogebra]);

    const handleElementConfigChange = useCallback((index, cfg) => {
        setResponses(prev => {
            const next = [...prev];
            next[index] = { ...next[index], element: { ...next[index].element, config: cfg } };
            return next;
        });
    }, [setResponses]);

    const handleAddResponse = useCallback(() => {
        if (responses.length >= MAX_RESPONSES) return;
        setResponses(prev => [...prev, emptyResponse()]);
    }, [responses.length, setResponses]);

    const handleRemoveResponse = useCallback((index) => {
        if (responses.length <= MIN_RESPONSES) return;
        setResponses(prev => prev.filter((_, i) => i !== index));
        // Recalculer les index des bonnes réponses
        setCorrectAnswerIndexes(prev => {
            const next = new Set();
            prev.forEach(idx => {
                if (idx < index) next.add(idx);
                else if (idx > index) next.add(idx - 1);
                // idx === index → supprimé, on l'enlève
            });
            return next;
        });
    }, [responses.length, setResponses, setCorrectAnswerIndexes]);

    return (
        <div className="p-3 sm:p-5 rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-sky-100 text-sky-800 text-[10px] font-extrabold flex items-center justify-center">2</span>
                    Réponses
                </h3>
                <div className="flex items-center gap-2">
                    {/* Toggle réponse unique / multiples */}
                    {setIsMulti && (
                        <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden mr-2">
                            <button
                                type="button"
                                onClick={() => handleToggleMulti(false)}
                                className={`px-2.5 py-1 text-[10px] font-medium transition-all ${
                                    !isMulti ? 'bg-blue-900 text-white' : 'text-gray-400 hover:text-gray-600'
                                }`}
                            >
                                Unique
                            </button>
                            <button
                                type="button"
                                onClick={() => handleToggleMulti(true)}
                                className={`px-2.5 py-1 text-[10px] font-medium transition-all ${
                                    isMulti ? 'bg-blue-900 text-white' : 'text-gray-400 hover:text-gray-600'
                                }`}
                            >
                                Multiples
                            </button>
                        </div>
                    )}
                    <span className="text-[10px] text-gray-400 font-medium">
                        {isMulti ? 'Cocher les bonnes réponses' : 'Cliquer la lettre = bonne réponse'}
                    </span>
                    <button
                        type="button"
                        onClick={onTogglePreview}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                            showPreview
                                ? 'bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200'
                                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                        }`}
                    >
                        {showPreview ? <PenLine size={13} /> : <Eye size={13} />}
                        {showPreview ? 'Éditer' : 'Aperçu'}
                    </button>
                </div>
            </div>

            {showPreview ? (
                <PreviewMode
                    responses={responses}
                    correctAnswerIndexes={correctAnswerIndexes}
                    isMulti={isMulti}
                    hasElementResponse={hasElementResponse}
                />
            ) : (
                <EditMode
                    responses={responses}
                    isMulti={isMulti}
                    correctAnswerIndexes={correctAnswerIndexes}
                    onSelectCorrect={handleSelectCorrect}
                    onTextChange={handleResponseTextChange}
                    onResetToText={handleResetToText}
                    onSetElement={handleSetElement}
                    onElementConfigChange={handleElementConfigChange}
                    onOpenGeogebra={onOpenGeogebra}
                    onAddResponse={handleAddResponse}
                    onRemoveResponse={handleRemoveResponse}
                />
            )}

            {/* Message d'erreur doublons */}
            {hasDuplicates && (
                <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                    <span>⚠</span> Certaines réponses sont identiques — chaque choix doit être unique.
                </p>
            )}
        </div>
    );
}

// ─── Aperçu (style élève) ───

const PreviewMode = memo(function PreviewMode({ responses, correctAnswerIndexes, isMulti, hasElementResponse }) {
    const hasFilled = responses.some(isResponseFilled);

    if (!hasFilled) {
        return (
            <div className="rounded-xl bg-slate-800 p-4">
                <p className="text-slate-500 text-sm italic">Aucune réponse remplie</p>
            </div>
        );
    }

    return (
        <div className="rounded-xl bg-slate-800 p-4">
            {isMulti && <p className="text-[10px] text-slate-400 mb-2 font-medium">Plusieurs réponses possibles</p>}
            <div className={`grid gap-2 ${hasElementResponse ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
                {responses.map((r, i) => {
                    if (!isResponseFilled(r)) return null;
                    const isCorrect = correctAnswerIndexes.has(i);
                    return (
                        <div
                            key={i}
                            className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border transition-all ${
                                isCorrect
                                    ? 'border-emerald-500/50 bg-emerald-500/10'
                                    : 'border-slate-600 bg-slate-700/50'
                            }`}
                        >
                            <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-extrabold flex-shrink-0 mt-0.5 ${
                                isCorrect ? 'bg-emerald-500 text-white' : 'bg-slate-600 text-slate-300'
                            }`}>
                                {LETTRES[i]}
                            </span>
                            <div className="flex-1 min-w-0">
                                <ChoixRenderer
                                    choix={r.mode === 'element' && r.element ? r.element : r.contenu}
                                    className={`text-sm ${isCorrect ? 'text-emerald-200 font-medium' : 'text-slate-200'}`}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

// ─── Édition (grille dynamique A→H) ───

// Grille adaptative selon le nombre de choix
const getEditGridClass = (count) => {
    if (count <= 4) return 'grid-cols-1 sm:grid-cols-2';
    if (count <= 6) return 'grid-cols-1 sm:grid-cols-3';
    return 'grid-cols-1 sm:grid-cols-4';
};

const EditMode = memo(function EditMode({
    responses,
    correctAnswerIndexes,
    onSelectCorrect,
    onTextChange,
    onResetToText,
    onSetElement,
    onElementConfigChange,
    onOpenGeogebra,
    onAddResponse,
    onRemoveResponse,
}) {
    return (
        <div className="space-y-2">
            <div className={`grid ${getEditGridClass(responses.length)} gap-2`}>
                {responses.map((response, i) => (
                    <ChoiceCard
                        key={i}
                        index={i}
                        letter={LETTRES[i]}
                        response={response}
                        isCorrect={correctAnswerIndexes.has(i)}
                        removable={responses.length > MIN_RESPONSES}
                        onSelect={onSelectCorrect}
                        onTextChange={onTextChange}
                        onResetToText={onResetToText}
                        onSetElement={onSetElement}
                        onElementConfigChange={onElementConfigChange}
                        onOpenGeogebra={onOpenGeogebra}
                        onRemove={onRemoveResponse}
                    />
                ))}
            </div>
            {responses.length < MAX_RESPONSES && (
                <button
                    type="button"
                    onClick={onAddResponse}
                    className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border-2 border-dashed border-gray-200 text-gray-400 hover:border-blue-300 hover:text-blue-500 hover:bg-blue-50/50 text-xs font-medium transition-all"
                >
                    <Plus size={14} />
                    Ajouter le choix {LETTRES[responses.length]}
                </button>
            )}
        </div>
    );
});

// ─── Carte choix individuel ───

const ChoiceCard = memo(function ChoiceCard({
    index,
    letter,
    response,
    isCorrect,
    removable,
    onSelect,
    onTextChange,
    onResetToText,
    onSetElement,
    onElementConfigChange,
    onOpenGeogebra,
    onRemove,
}) {
    const isElement = response.mode === 'element';
    const ElementEditor = isElement && response.element ? ELEMENT_EDITORS[response.element.type] : null;

    return (
        <div className={`rounded-lg transition-all duration-200 ${
            isCorrect
                ? 'border-2 border-emerald-500 bg-emerald-50/50'
                : 'border border-gray-200 bg-white hover:border-gray-300'
        }`}>
            {/* En-tête : badge lettre + toggle mode */}
            <div className="flex items-center gap-2 px-2.5 py-2">
                <button
                    type="button"
                    onClick={() => onSelect(index)}
                    title={isCorrect ? 'Bonne réponse' : 'Définir comme bonne réponse'}
                    className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-extrabold flex-shrink-0 transition-all duration-200 ${
                        isCorrect
                            ? 'bg-emerald-500 text-white shadow-sm'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                >
                    {isCorrect ? <Check size={14} strokeWidth={3} /> : letter}
                </button>

                {isElement ? (
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <span className="text-[10px] text-gray-400 truncate">
                            {RESPONSE_ELEMENT_TYPES.find(t => t.type === response.element?.type)?.label || 'Élément'}
                        </span>
                        <div className="ml-auto flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => onResetToText(index)}
                                className="text-[10px] text-gray-400 hover:text-red-500 transition"
                                title="Revenir au texte"
                            >
                                <X size={12} />
                            </button>
                            {removable && (
                                <button
                                    type="button"
                                    onClick={() => onRemove(index)}
                                    title={`Supprimer le choix ${letter}`}
                                    className="w-6 h-6 rounded flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition"
                                >
                                    <Minus size={12} />
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <>
                        <input
                            type="text"
                            value={response.contenu}
                            onChange={(e) => onTextChange(index, e.target.value)}
                            placeholder={`Réponse ${letter}`}
                            className="flex-1 py-1 px-0 border-none bg-transparent text-sm text-gray-700 placeholder-gray-300 focus:outline-none min-w-0"
                        />
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                            {RESPONSE_ELEMENT_TYPES.map(et => (
                                <button
                                    key={et.type}
                                    type="button"
                                    onClick={() => onSetElement(index, et.type)}
                                    title={et.label}
                                    className="w-6 h-6 rounded flex items-center justify-center text-gray-300 hover:text-sky-500 hover:bg-sky-50 transition"
                                >
                                    {et.icon}
                                </button>
                            ))}
                            {removable && (
                                <button
                                    type="button"
                                    onClick={() => onRemove(index)}
                                    title={`Supprimer le choix ${letter}`}
                                    className="w-6 h-6 rounded flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition ml-0.5"
                                >
                                    <Minus size={12} />
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Éditeur d'élément */}
            {isElement && response.element && (
                <div className="px-2.5 pb-2.5">
                    {ElementEditor ? (
                        <ElementEditor
                            config={response.element.config}
                            onConfigChange={(cfg) => onElementConfigChange(index, cfg)}
                        />
                    ) : response.element.type === 'geogebra' ? (
                        <div
                            onClick={() => onOpenGeogebra?.(index)}
                            className="relative group/ggb cursor-pointer rounded-lg overflow-hidden border border-gray-200 hover:border-sky-300 transition-all"
                        >
                            {response.element.config?.ggb_svg ? (
                                <div
                                    className="bg-white p-1 [&_svg]:block [&_svg]:max-w-full [&_svg]:h-auto [&_svg]:max-h-32"
                                    dangerouslySetInnerHTML={{ __html: sanitizeSvg(response.element.config.ggb_svg) }}
                                />
                            ) : response.element.config?.ggb_image ? (
                                <img
                                    src={`data:image/png;base64,${response.element.config.ggb_image}`}
                                    alt="Figure GeoGebra"
                                    className="w-full h-auto max-h-32 object-contain bg-white"
                                />
                            ) : (
                                <div className="flex flex-col items-center justify-center h-24 bg-gray-50 text-gray-400 gap-1.5">
                                    <Triangle size={20} />
                                    <span className="text-[10px] font-medium">Dessiner la figure</span>
                                </div>
                            )}
                            <div className="absolute inset-0 bg-black/0 group-hover/ggb:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover/ggb:opacity-100">
                                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-white/90 backdrop-blur rounded-lg text-xs font-medium text-gray-700 shadow-sm">
                                    <Pencil size={12} />
                                    {response.element.config?.ggb_base64 ? 'Modifier' : 'Dessiner'}
                                </span>
                            </div>
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
});

export { LETTRES, emptyResponse, isResponseFilled, RESPONSE_ELEMENT_TYPES };
export default memo(QCMResponseEditor);
